<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { facebookImportsAPI } from 'dashboard/api/facebookImports';
import ImportProgressCard from './ImportProgressCard.vue';
import ImportLogTable from './ImportLogTable.vue';
import FacebookSyncSettingsPanel from './FacebookSyncSettingsPanel.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();
const showAlert = useAlert();

const inboxId = computed(() => route.params.inboxId);
const activeTab = ref('import');

const TABS = computed(() => [
  { key: 'import', label: t('FACEBOOK_IMPORT.TABS.IMPORT') },
  { key: 'sync', label: t('FACEBOOK_IMPORT.TABS.SYNC') },
  { key: 'log', label: t('FACEBOOK_IMPORT.TABS.LOG') },
]);

const IMPORT_MODES = ['full', 'date_range', 'incremental'];

const importMode = ref('full');
const dateFrom = ref('');
const dateTo = ref('');
const minMessages = ref(1);

const estimateResult = ref(null);
const isEstimating = ref(false);
const isStarting = ref(false);

const imports = computed(() => store.getters['facebookImports/getImports']);
const uiFlags = computed(() => store.getters['facebookImports/getUIFlags']);

const activeJob = computed(() =>
  imports.value.find(j => ['pending', 'running', 'paused'].includes(j.status))
);

const latestJob = computed(() => imports.value[0] || null);
const logs = computed(() => latestJob.value?.conversation_logs || []);

async function estimate() {
  isEstimating.value = true;
  estimateResult.value = null;
  try {
    const { data } = await facebookImportsAPI.estimate(inboxId.value);
    estimateResult.value = data;
  } catch (e) {
    const message = e?.response?.data?.error || e?.message || 'Unknown error';
    estimateResult.value = { error: true, message };
  } finally {
    isEstimating.value = false;
  }
}

async function startImport() {
  isStarting.value = true;
  try {
    const payload = {
      inbox_id: inboxId.value,
      import_mode: importMode.value,
      filters: {
        min_message_count: minMessages.value,
        ...(importMode.value === 'date_range' && {
          date_from: dateFrom.value,
          date_to: dateTo.value,
        }),
      },
    };
    await store.dispatch('facebookImports/create', payload);
    showAlert(t('FACEBOOK_IMPORT.ALERTS.IMPORT_STARTED'));
    await store.dispatch('facebookImports/fetch', inboxId.value);
    activeTab.value = 'import';
  } finally {
    isStarting.value = false;
  }
}

onMounted(() => store.dispatch('facebookImports/fetch', inboxId.value));
</script>

<template>
  <div class="max-w-2xl p-4">
    <h2 class="mb-1 text-base font-semibold text-n-slate-12">
      {{ t('FACEBOOK_IMPORT.TITLE') }}
    </h2>
    <p class="mb-4 text-sm text-n-slate-11">
      {{ t('FACEBOOK_IMPORT.SUBTITLE') }}
    </p>

    <div class="mb-4 flex gap-1 border-b border-n-weak">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        class="px-4 py-2 text-sm transition-colors"
        :class="
          activeTab === tab.key
            ? 'border-b-2 border-woot-500 font-medium text-woot-600'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="activeTab === 'import'">
      <ImportProgressCard
        v-if="activeJob"
        :import-job="activeJob"
        :inbox-id="inboxId"
      />

      <div
        v-if="!activeJob || activeJob.status === 'paused'"
        class="space-y-4 rounded-lg border border-n-weak p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('FACEBOOK_IMPORT.TABS.IMPORT') }}
        </h3>

        <div>
          <p class="mb-1 text-xs text-n-slate-11">
            {{ t('FACEBOOK_IMPORT.IMPORT_MODE.LABEL') }}
          </p>
          <div class="space-y-1">
            <label
              v-for="mode in IMPORT_MODES"
              :key="mode"
              class="flex cursor-pointer items-center gap-2 text-sm"
            >
              <input v-model="importMode" type="radio" :value="mode" />
              {{ t(`FACEBOOK_IMPORT.IMPORT_MODE.${mode}`) }}
            </label>
          </div>
        </div>

        <div v-if="importMode === 'date_range'" class="flex gap-3">
          <div class="flex-1">
            <label class="text-xs text-n-slate-11">
              {{ t('FACEBOOK_IMPORT.FILTERS.DATE_FROM') }}
            </label>
            <input
              v-model="dateFrom"
              type="date"
              class="mt-1 w-full rounded border border-n-weak bg-n-slate-1 px-2 py-1.5 text-sm"
            />
          </div>
          <div class="flex-1">
            <label class="text-xs text-n-slate-11">
              {{ t('FACEBOOK_IMPORT.FILTERS.DATE_TO') }}
            </label>
            <input
              v-model="dateTo"
              type="date"
              class="mt-1 w-full rounded border border-n-weak bg-n-slate-1 px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        <div>
          <label class="text-xs text-n-slate-11">
            {{ t('FACEBOOK_IMPORT.FILTERS.MIN_MESSAGES') }}
          </label>
          <input
            v-model.number="minMessages"
            type="number"
            min="0"
            class="mt-1 block w-24 rounded border border-n-weak bg-n-slate-1 px-2 py-1.5 text-sm"
          />
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{ t('FACEBOOK_IMPORT.FILTERS.MIN_MESSAGES_HINT') }}
          </p>
        </div>

        <div>
          <button
            class="rounded border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-slate-3 disabled:opacity-50"
            :disabled="isEstimating"
            @click="estimate"
          >
            {{
              isEstimating
                ? t('FACEBOOK_IMPORT.BUTTONS.ESTIMATING')
                : t('FACEBOOK_IMPORT.BUTTONS.ESTIMATE')
            }}
          </button>
          <p
            v-if="estimateResult && !estimateResult.error"
            class="mt-1 text-xs text-n-slate-11"
          >
            {{
              t('FACEBOOK_IMPORT.ESTIMATE.RESULT', {
                count: estimateResult.sample_count,
              })
            }}
            <span v-if="estimateResult.has_next_page">
              {{ t('FACEBOOK_IMPORT.ESTIMATE.HAS_MORE') }}
            </span>
          </p>
          <p
            v-else-if="estimateResult && estimateResult.error"
            class="mt-1 text-xs text-red-500"
          >
            {{ estimateResult.message || t('FACEBOOK_IMPORT.ESTIMATE.ERROR') }}
          </p>
        </div>

        <button
          class="rounded bg-woot-500 px-4 py-2 text-sm text-white hover:bg-woot-600 disabled:opacity-50"
          :disabled="isStarting || uiFlags.isCreating"
          @click="startImport"
        >
          {{
            isStarting
              ? t('FACEBOOK_IMPORT.BUTTONS.STARTING')
              : t('FACEBOOK_IMPORT.BUTTONS.START')
          }}
        </button>
      </div>

      <div v-if="imports.length > 0 && !activeJob" class="mt-4">
        <h4 class="mb-2 text-xs font-medium text-n-slate-11">
          {{ t('FACEBOOK_IMPORT.RECENT_IMPORTS') }}
        </h4>
        <div
          v-for="job in imports.slice(0, 3)"
          :key="job.id"
          class="flex items-center justify-between border-b border-n-weak py-1.5 text-sm"
        >
          <span class="text-n-slate-12">
            {{ new Date(job.created_at).toLocaleDateString() }}
            {{
              t('FACEBOOK_IMPORT.CONVOS_IMPORTED', {
                count: job.conversations_imported,
              })
            }}
          </span>
          <span
            class="rounded-full px-2 py-0.5 text-xs"
            :class="{
              'bg-green-100 text-green-700': job.status === 'completed',
              'bg-red-100 text-red-700':
                job.status === 'failed' || job.status === 'cancelled',
              'bg-n-slate-100 text-n-slate-600': ![
                'completed',
                'failed',
                'cancelled',
              ].includes(job.status),
            }"
          >
            {{ t(`FACEBOOK_IMPORT.STATUS.${job.status}`) }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'sync'">
      <FacebookSyncSettingsPanel :inbox-id="inboxId" />
    </div>

    <div v-if="activeTab === 'log'">
      <ImportLogTable :logs="logs" />
    </div>
  </div>
</template>
